import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/shared/AuthContext';
import { useBranding } from '@/contexts/shared/BrandingContext';
import axiosInstance from '@/api/axiosInstance';
import { USER_ENDPOINTS } from '@/api/endpoints/user.endpoints';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  allowedThemes: Theme[];
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  setTheme: () => {},
  allowedThemes: ['light', 'dark'],
  isLoading: true,
});

export const useTheme = () => useContext(ThemeContext);

// ── DOM helper ───────────────────────────────────────────────────────────────
const applyTheme = (theme: Theme) => {
  const html = document.documentElement;
  html.setAttribute('data-theme', theme);
  // index.html paints an inline canvas colour before React loads; drop it so
  // the token-driven background follows later theme switches.
  html.style.removeProperty('background-color');
  html.style.removeProperty('color');
};

// ── Local-storage keys ───────────────────────────────────────────────────────
// LS_KEY: last applied theme — read by the pre-paint script in index.html.
const LS_KEY = 'ilearn_theme';
// PENDING_KEY: a theme the person explicitly picked that the server hasn't
// confirmed yet, as "<userId>:<theme>" ("guest:<theme>" when picked signed
// out, e.g. on the guest homepage). It outranks the server value on the next
// load, so a choice survives a PATCH lost to a reload/tab close/network error
// and a choice made before signing in carries into the account.
const PENDING_KEY = 'ilearn_theme_pending';

const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark';

const lsGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const lsSet = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* storage blocked */ }
};
const lsRemove = (key: string) => {
  try { localStorage.removeItem(key); } catch { /* storage blocked */ }
};

/** The theme a signed-out visitor has chosen or last used, if any. */
export const ownSignedOutTheme = (): Theme | null => {
  const pending = readPending();
  if (pending) return pending.theme;
  const stored = lsGet(LS_KEY);
  return isTheme(stored) ? stored : null;
};

const readPending = (): { owner: string; theme: Theme } | null => {
  const raw = lsGet(PENDING_KEY);
  if (!raw) return null;
  const i = raw.lastIndexOf(':');
  const owner = raw.slice(0, i);
  const theme = raw.slice(i + 1);
  return i > 0 && isTheme(theme) ? { owner, theme } : null;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { branding } = useBranding();
  const { pathname } = useLocation();
  // The login screen forces the site default theme (see below); leaving it
  // must restore the person's own theme, so the resolver re-runs on this flag.
  const onLoginPage = pathname.startsWith('/login');

  const [theme, setThemeState] = useState<Theme>(() => {
    // Read whatever theme is currently on the DOM (index.html's pre-paint
    // script applied the cached value). Fall back to localStorage, then 'light'.
    const domTheme = document.documentElement.getAttribute('data-theme');
    const stored = lsGet(LS_KEY);
    const initial: Theme = isTheme(domTheme) ? domTheme : isTheme(stored) ? stored : 'light';
    applyTheme(initial);
    return initial;
  });
  const [isLoading, setIsLoading] = useState(true);
  // Bumped on every explicit choice, so a preferences GET that was already in
  // flight can't overwrite what the person just picked.
  const choiceSeq = useRef(0);

  // Derive allowed themes from branding settings
  const allowedThemes: Theme[] = (() => {
    const raw = (branding as any)?.allowed_themes;
    if (Array.isArray(raw) && raw.length > 0) {
      const valid = raw.filter(isTheme);
      if (valid.length > 0) return valid;
    }
    return ['light', 'dark'];
  })();

  // Tenant default theme (fallback when user has no saved preference)
  const tenantDefault: Theme = (() => {
    const raw = (branding as any)?.default_theme;
    return isTheme(raw) ? raw : 'light';
  })();

  const userId = user?.id != null ? String(user.id) : null;

  // ── Save a choice to the server; clear the pending marker once it lands ──
  // Saves run one after another so rapid toggles can't land out of order.
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());
  const qc = useQueryClient();
  const saveToServer = useCallback((newTheme: Theme, owner: string) => {
    saveChain.current = saveChain.current
      .then(() => axiosInstance.patch(USER_ENDPOINTS.MY_PREFERENCES, { theme: newTheme }))
      .then(() => {
        qc.setQueryData(['my-preferences', owner], { theme: newTheme });
        const p = readPending();
        if (p && p.theme === newTheme && (p.owner === owner || p.owner === 'guest')) {
          lsRemove(PENDING_KEY);
        }
      })
      .catch(err => console.error('[ThemeContext] Failed to save theme preference:', err));
  }, [qc]);

  // ── Resolve the theme on mount / user change / leaving the login page ────
  useEffect(() => {
    const allowed = (t: unknown): t is Theme => isTheme(t) && allowedThemes.includes(t);
    const pending = readPending();

    // Login screen: keep the theme the visitor already chose (e.g. dark on the
    // guest homepage, or their last in-app theme) — switching to the site
    // default on the way to sign-in was jarring. With no choice yet, LoginPage
    // applies the tenant default from its public branding.
    if (onLoginPage) {
      const own = ownSignedOutTheme();
      if (own) {
        applyTheme(own);
        setThemeState(own);
      }
      setIsLoading(false);
      return;
    }

    if (!userId) {
      // Signed out → the visitor's own choice, else the cached theme, else tenant default
      const stored = lsGet(LS_KEY);
      const resolved: Theme =
        pending && allowed(pending.theme) ? pending.theme
        : allowed(stored) ? stored
        : tenantDefault;
      applyTheme(resolved);
      setThemeState(resolved);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const seqAtStart = choiceSeq.current;
    setIsLoading(true);

    // An unsaved explicit choice by this person (or made signed-out just
    // before signing in) is newer than whatever the server holds.
    const ownPending =
      pending && (pending.owner === userId || pending.owner === 'guest') && allowed(pending.theme)
        ? pending.theme
        : null;
    if (pending && !ownPending) lsRemove(PENDING_KEY); // another account's leftover

    // Through the query cache, so a provider that re-mounts within seconds (e.g. when updated translations
    // arrive) reuses the answer instead of asking again (dashboard audit 2026-11-02).
    qc.fetchQuery({
      queryKey: ['my-preferences', userId],
      queryFn: async () => (await axiosInstance.get<{ theme: Theme | null }>(USER_ENDPOINTS.MY_PREFERENCES)).data,
      staleTime: 10_000,
    })
      .then(res => {
        if (cancelled || choiceSeq.current !== seqAtStart) return;
        const serverTheme = res?.theme;
        let resolved: Theme;
        if (ownPending) {
          resolved = ownPending;
          if (serverTheme !== ownPending) saveToServer(ownPending, userId);
          else lsRemove(PENDING_KEY);
        } else if (allowed(serverTheme)) {
          resolved = serverTheme;
        } else {
          resolved = tenantDefault;
        }
        lsSet(LS_KEY, resolved);
        applyTheme(resolved);
        setThemeState(resolved);
      })
      .catch(() => {
        if (cancelled || choiceSeq.current !== seqAtStart) return;
        // API failed → the unsaved choice, else the cached theme, else tenant default
        const stored = lsGet(LS_KEY);
        const resolved: Theme = ownPending ?? (allowed(stored) ? stored : tenantDefault);
        applyTheme(resolved);
        setThemeState(resolved);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
    // Re-run when the user identity changes or the login page is entered/left
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, onLoginPage]);

  // ── If tenant restricts themes and current theme is no longer allowed ─────
  useEffect(() => {
    if (!isLoading && !onLoginPage && !allowedThemes.includes(theme)) {
      const fallback = tenantDefault;
      applyTheme(fallback);
      setThemeState(fallback);
      lsSet(LS_KEY, fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedThemes.join(',')]);

  const setTheme = useCallback((newTheme: Theme) => {
    choiceSeq.current += 1;
    // Immediately update DOM + state for instant feedback
    applyTheme(newTheme);
    setThemeState(newTheme);
    lsSet(LS_KEY, newTheme);

    // Remember the choice until the server confirms it, then save right away
    // (a debounced save was lost when the page reloaded/closed within 300 ms).
    const owner = userId ?? 'guest';
    lsSet(PENDING_KEY, `${owner}:${newTheme}`);
    if (userId) saveToServer(newTheme, userId);
  }, [userId, saveToServer]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, allowedThemes, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};
